import { useSelector } from "react-redux";
import CustomerReviewAction from "@common/redux/actions/CustomerReviewAction";
import ConfirmModalAction from "@common/redux/actions/ConfirmModalAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import ReviewClient from "@common/api/ReviewClient";
import CustomerClient from "@common/api/CustomerClient";
import { handleError } from "@common/components/helpers/error";
import { customerInteractionReviewSchema } from "@common/components/helpers/validation-schemas/customerInteractionReviewSchema";
import CustomerReviewModalAction from "@common/redux/actions/CustomerReviewModalAction";

function useCustomerReview() {
  const data = useSelector((state) => CustomerReviewAction.getStatePath(state));
  const { selectedRecord, selectedReview, form } = data;

  const modal = useSelector((state) =>
    CustomerReviewModalAction.getStatePath(state),
  );
  const { selectedCustomer } = modal;

  // Customer Review Form Modal
  const setReviewModalState = CustomerReviewAction.dispatch;

  const onChange = (_, { name, value }) =>
    setReviewModalState({ form: { ...form, [name]: value } });

  const onOpenClick = () => {
    onRecordChange(selectedRecord);
    CustomerReviewAction.open();
  };

  const onReviewChange = (_, { value }) =>
    setReviewModalState({ selectedReview: value });

  const onRecordChange = (selectedRecord) => {
    setReviewModalState({
      selectedRecord,
      selectedReview: selectedRecord?.compliance?.reviews?.[0]?.id ?? null,
    });
  };

  const resetCustomerReviewForm = () =>
    setReviewModalState({
      form: {
        enteredVenue: null,
        venueEnterCountId: null,
        venueEnterTimeId: null,
        behaviourChanged: null,
        behaviourComments: null,
        atRisk: null,
        atRiskMeasures: null,
        anotherReview: null,
        nextReviewDate: null,
        barCustomer: null,
      },
    });

  const onFormLoad = () => {
    CustomerClient.GetReviewMeta().then((metaResponse) => {
      setReviewModalState({ ...metaResponse });
    });

    if (!selectedReview) {
      resetCustomerReviewForm();
      return;
    }

    ReviewClient.UpdateMetadata(selectedReview?.record?.id).then(
      (response) => {
        setReviewModalState({
          ...response,
          form: { ...response?.record },
        });
      },
      (error) => handleError(error),
    );
  };

  const onConfirmComplete = () => {
    CustomerClient.CompleteReview({
      customerId: selectedCustomer?.id,
      id: selectedReview?.record?.id ?? 0,
      rowVersion: selectedCustomer?.rowVersion,
      form,
    }).then(
      (response) => {
        AlertModalAction.open(
          "Review successfully completed!",
          "Success",
          () => {
            setReviewModalState({ reviewCompleted: true });
            CustomerReviewAction.reset();
          },
        );
      },
      (error) => {
        console.error(error);
        handleError(error);
      },
    );
  };

  const onCompleteClick = () => {
    const schema = customerInteractionReviewSchema(form);
    schema
      .validate(form, { abortEarly: false })
      .then(
        () => {
          ConfirmModalAction.open(
            "Are you sure you want to complete this review?",
            "Confirm",
            onConfirmComplete,
          );
        },
        (err) => {
          const errors = {};
          err.inner.forEach((error) => {
            errors[error.path] = error.message;
          });

          setReviewModalState({ errors });
        },
      )
      .finally(() => setReviewModalState({ inEditMode: false }));
  };

  return {
    ...data,
    modal,
    onRecordChange,
    onReviewChange,
    onCompleteClick,
    onFormLoad,
    setReviewModalState,
    onOpenClick,
    onChange,
  };
}

export default useCustomerReview;
